import * as Dialog from '@radix-ui/react-dialog';
import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface ModalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  /** Кнопки внизу (например «Сохранить» / «Отмена»). */
  footer?: React.ReactNode;
  className?: string;
  dataTestId?: string;
  /** Флаг несохранённых данных (если не передан, отслеживается ввод внутри формы). */
  isDirty?: boolean;
}

/** Центрированное модальное окно (в отличие от правого `DetailDrawer`). */
export function ModalDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
  dataTestId,
  isDirty
}: ModalDialogProps) {
  const [hasInput, setHasInput] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setHasInput(false);
    }
  }, [open]);

  const dirty = isDirty !== undefined ? isDirty : hasInput;

  const handleRequestClose = React.useCallback(() => {
    if (dirty) {
      const ok = window.confirm('В форме есть несохранённые данные. Закрыть окно без сохранения?');
      if (!ok) return;
    }
    onOpenChange(false);
  }, [dirty, onOpenChange]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleRequestClose();
        } else {
          onOpenChange(true);
        }
      }}
    >
      <Dialog.Portal>
        {/* z выше DetailDrawer (5301), поповеров (≈5600) и прочих панелей staff — иначе подтверждения не видны поверх UI */}
        <Dialog.Overlay className="fixed inset-0 z-[20000] bg-black/55 backdrop-blur-[2px]" />
        <Dialog.Content
          data-testid={dataTestId}
          onPointerDownOutside={(e) => {
            if (dirty) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (dirty) {
              const ok = window.confirm('В форме есть несохранённые данные. Закрыть окно без сохранения?');
              if (!ok) {
                e.preventDefault();
              }
            }
          }}
          onInput={() => {
            if (!hasInput) setHasInput(true);
          }}
          onChange={() => {
            if (!hasInput) setHasInput(true);
          }}
          className={cn(
            'fixed left-1/2 top-1/2 z-[20001] w-[calc(100%-1.5rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-sm border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] p-4 sm:p-6 shadow-xl outline-none',
            className
          )}
          {...(description?.trim() ? {} : { 'aria-describedby': undefined })}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-semibold leading-tight text-[hsl(var(--ui-card-foreground))]">
                {title}
              </Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-1 text-sm text-[hsl(var(--ui-muted-foreground))]">
                  {description}
                </Dialog.Description>
              ) : null}
            </div>
            <button
              type="button"
              onClick={handleRequestClose}
              className="shrink-0 rounded-[var(--ui-radius-control)] p-2 text-[hsl(var(--ui-muted-foreground))] hover:bg-[hsl(var(--ui-muted))] hover:text-[hsl(var(--ui-foreground))]"
              aria-label="Закрыть"
            >
              <X className="size-4" aria-hidden />
            </button>
          </div>
          <div className="mt-4 text-sm text-[hsl(var(--ui-card-foreground))]">{children}</div>
          {footer ? (
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">{footer}</div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
